import { ADMIN_API_BASE_URL as API_BASE_URL } from "../config";
import { handleApiResponse } from "../utils/commonfunction";
import { makeApiRequest, addAppTypeToParams, addAppTypeToBody } from "../utils/apiHelper";

class KYCService {
  private getAuthHeaders(): HeadersInit {
    const token = localStorage.getItem("adminToken");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    };
  }

  async getKYCQueue(
    page: number = 1,
    perPage: number = 10,
    search = "",
    docType = "",
    dateFrom = "",
    dateTo = "",
    sortField = "created_at",
    sortDirection = "desc",
    statusFilter = "pending"
  ) {
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
        sort_field: sortField,
        sort_direction: sortDirection,
        status_filter: statusFilter,
      });
      if (search) params.append("search", search);
      if (docType) params.append("doc_type", docType);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      const paramsWithAppType = addAppTypeToParams(params);
      
      return makeApiRequest(`/kyc-queue?${paramsWithAppType.toString()}`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Error fetching KYC queue:", error);
      throw error;
    }
  }

  async getKYCDocuments(userId: string) {
    try {
      return makeApiRequest(`/app-users/${userId}/kyc-documents`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Error fetching KYC documents:", error);
      throw error;
    }
  }

  async approveKYC(userId: string) {
    try {
      return makeApiRequest(`/app-users/${userId}/kyc-approve`, {
        method: "POST",
      });
    } catch (error) {
      console.error("Error approving KYC:", error);
      throw error;
    }
  }

  async rejectKYC(userId: string, reason: string, notes?: string) {
    try {
      const bodyWithAppType = addAppTypeToBody({
        reason,
        notes,
      });
      
      return makeApiRequest(`/app-users/${userId}/kyc-reject`, {
        method: "POST",
        body: JSON.stringify(bodyWithAppType),
      });
    } catch (error) {
      console.error("Error rejecting KYC:", error);
      throw error;
    }
  }

  async bulkDownloadKycDocuments(userIds: string[]): Promise<Blob> {
    const bodyWithAppType = addAppTypeToBody({ user_ids: userIds });
    
    const token = localStorage.getItem("adminToken");
    const response = await fetch(
      `${API_BASE_URL}/kyc-documents/bulk-download`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(bodyWithAppType),
      }
    );
    return handleApiResponse(response)
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    return await response.blob();
  }

  async getKYCAnalytics(startDate?: string, endDate?: string) {
    const params = new URLSearchParams();
    if (startDate) params.append("start_date", startDate);
    if (endDate) params.append("end_date", endDate);
    
    const paramsWithAppType = addAppTypeToParams(params);
    
    return makeApiRequest(
      `/kyc-analytics${
        paramsWithAppType.toString() ? "?" + paramsWithAppType.toString() : ""
      }`,
      {
        method: "GET",
      }
    );
  }

  async getRejectedKYC(
    page: number = 1,
    perPage: number = 10,
    search = "",
    dateFrom = "",
    dateTo = ""
  ) {
    try {
      const params = new URLSearchParams({
        page: String(page),
        per_page: String(perPage),
      });
      if (search) params.append("search", search);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);

      const paramsWithAppType = addAppTypeToParams(params);

      return makeApiRequest(`/kyc-rejected?${paramsWithAppType.toString()}`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Error fetching rejected KYC:", error);
      throw error;
    }
  }

  async getKYCHistory(userId: string) {
    try {
      return makeApiRequest(`/app-users/${userId}/kyc-history`, {
        method: "GET",
      });
    } catch (error) {
      console.error("Error fetching KYC history:", error);
      throw error;
    }
  }
}

export const kycService = new KYCService();
